/**
 * Cast Assignment Selectors
 *
 * Pure utility functions to derive relationships from CastAssignment records.
 * These are the canonical way to query actor↔character relationships.
 *
 * Legacy fields `Character.actorId` and `Personnel.characterIds` are still
 * maintained for backward compatibility via sync in `personnel/store.ts`, but
 * NEW code should prefer these selectors for multi-role awareness.
 */
import type { CastAssignment, CastingRole } from '../types';

/**
 * Get the primary actor ID for a character.
 * Returns the personnelId of the first 'primary' assignment, or undefined.
 */
export function getPrimaryActorId(
    characterId: string,
    assignments: CastAssignment[]
): string | undefined {
    return assignments.find(
        a => a.characterId === characterId && a.role === 'primary'
    )?.personnelId;
}

/**
 * Get all character IDs assigned to a personnel member (any role).
 * Returns unique character IDs.
 */
export function getCharacterIdsForPersonnel(
    personnelId: string,
    assignments: CastAssignment[]
): string[] {
    const ids = new Set<string>();
    for (const a of assignments) {
        if (a.personnelId === personnelId) ids.add(a.characterId);
    }
    return [...ids];
}

/**
 * Get all assignments for a personnel member, grouped by role.
 */
export function getAssignmentsByRole(
    personnelId: string,
    assignments: CastAssignment[]
): Record<CastingRole, CastAssignment[]> {
    const result: Record<CastingRole, CastAssignment[]> = {
        primary: [],
        understudy: [],
        swing: [],
        standby: [],
    };
    for (const a of assignments) {
        if (a.personnelId === personnelId) {
            result[a.role].push(a);
        }
    }
    return result;
}

/**
 * Get all cover (non-primary) assignments for a character.
 * Returns understudy, swing, and standby assignments.
 */
export function getCoversForCharacter(
    characterId: string,
    assignments: CastAssignment[]
): CastAssignment[] {
    return assignments.filter(
        a => a.characterId === characterId && a.role !== 'primary'
    );
}

/**
 * Get all actors assigned to a specific character (any role).
 * Returns an array of { personnelId, role } tuples.
 */
export function getActorsForCharacter(
    characterId: string,
    assignments: CastAssignment[]
): { personnelId: string; role: CastingRole }[] {
    return assignments
        .filter(a => a.characterId === characterId)
        .map(a => ({ personnelId: a.personnelId, role: a.role }));
}

/**
 * Build a Map<personnelId, characterName[]> using CastAssignment records.
 * Useful for displaying character names alongside actor names.
 */
export function buildActorCharacterMap(
    assignments: CastAssignment[],
    characters: { id: string; name: string; deleted?: boolean }[]
): Map<string, string[]> {
    const map = new Map<string, string[]>();
    const charLookup = new Map(characters.filter(c => !c.deleted).map(c => [c.id, c.name]));

    for (const a of assignments) {
        if (a.role !== 'primary') continue; // Typically we show primary characters
        const charName = charLookup.get(a.characterId);
        if (!charName) continue;
        if (!map.has(a.personnelId)) map.set(a.personnelId, []);
        map.get(a.personnelId)!.push(charName);
    }

    return map;
}

/**
 * Get required actor IDs for a set of scene IDs using CastAssignment records.
 * This replaces the legacy pattern of: characters.filter(c => c.sceneIds.includes(sid) && c.actorId)
 */
export function getRequiredActorIdsForScenes(
    sceneIds: string[],
    characters: { id: string; sceneIds: string[]; deleted?: boolean }[],
    assignments: CastAssignment[]
): Set<string> {
    const actorIds = new Set<string>();
    const sceneIdSet = new Set(sceneIds);

    // Find characters that appear in any of the given scenes
    const relevantCharIds = new Set<string>();
    for (const char of characters) {
        if (char.deleted) continue;
        for (const sid of char.sceneIds) {
            if (sceneIdSet.has(sid)) {
                relevantCharIds.add(char.id);
                break;
            }
        }
    }

    // Find primary actors for those characters
    for (const a of assignments) {
        if (a.role === 'primary' && relevantCharIds.has(a.characterId)) {
            actorIds.add(a.personnelId);
        }
    }

    return actorIds;
}
